/* Pet Cameo storefront adapter: wires the design's window.PetCameoAPI contract
   (see Website/API-Handoff.md) to the Pet Cameo app proxy (/apps/ppm/*) and Shopify's Ajax cart.
   No API keys here: every PP API call goes through the app proxy, which injects the key server-side. */
(() => {
  'use strict';
  const config = window.PetCameoConfig || {};
  const proxy = config.proxy || '/apps/ppm';
  const routes = config.routes || {};

  const wait = ms => new Promise(resolve => setTimeout(resolve, ms));
  const slug = value => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  const sizeKey = name => /^size$/i.test(name);
  const colorKey = name => /^colou?r$/i.test(name);

  async function getJson(url, init) {
    const response = await fetch(url, init);
    if (!response.ok) throw new Error('Request failed (' + response.status + ')');
    return response.json();
  }

  // ---- generation -------------------------------------------------------------------------
  let specsPromise = null;
  function loadSpecs() {
    if (!specsPromise) {
      specsPromise = getJson(proxy + '/store-specs')
        .then(data => (data.store_specs || []).filter(spec => spec.available !== false))
        .catch(error => { specsPromise = null; throw error; });
    }
    return specsPromise;
  }
  function setStatus(text) {
    const node = document.getElementById('generation-status');
    if (node) node.textContent = text;
  }

  async function startJob(id, options, photo) {
    const body = new FormData();
    body.append('id', id);
    body.append('options', JSON.stringify(options));
    body.append('photo', photo);
    const response = await fetch(proxy + '/create', { method: 'POST', body });
    if (response.status === 429) {
      const retryAfter = response.headers.get('Retry-After');
      throw new Error(retryAfter ? 'We’re busy right now. Please try again in about ' + retryAfter + ' seconds.' : 'We’re busy right now. Please try again shortly.');
    }
    if (!response.ok) throw new Error('Could not start your artwork (' + response.status + '). Please try again.');
    return response.json();
  }

  // Queue status: shows exactly what each poll returned (position, eta_seconds); there is no local
  // countdown between polls. An eta of 1s or less reads "almost done…" instead of "1s".
  function createQueueStatus() {
    return {
      update(job) {
        if (typeof job.position !== 'number') return;
        let eta = '';
        if (typeof job.eta_seconds === 'number') {
          const left = Math.ceil(job.eta_seconds);
          eta = left <= 1 ? ' (almost done…)' : left < 60 ? ' (' + left + 's left)' : ' (about ' + Math.ceil(left / 60) + ' min left)';
        }
        setStatus((job.position === 0 ? "You're up!" : 'Position in line: ' + job.position) + eta);
      },
      stop() {}
    };
  }

  async function pollJob(jobId, delay) {
    const queue = createQueueStatus();
    try {
      for (;;) {
        const job = await getJson(proxy + '/jobs/' + encodeURIComponent(jobId));
        if (job.state === 'done') return job;
        if (job.state === 'failed') throw new Error('The artwork could not be created. Please try again.');
        queue.update(job);
        delay = job.poll_after_ms || delay || 2000;
        await wait(delay);
      }
    } finally { queue.stop(); }
  }

  // The create form is generated from the API's store specs, so the page asks for them as they are.
  async function getStyles() {
    return loadSpecs();
  }

  // request: { photo, storeSpec: <spec filename>, options: { <field name>: value } }.
  // The form builds `options` from the spec's own field names, so nothing is mapped here.
  async function generateArtwork(request) {
    const specs = await loadSpecs();
    const spec = specs.find(item => item.filename === request.storeSpec);
    if (!spec) throw new Error('That style isn’t available right now. Please pick another one.');
    const options = { ...request.options, store_spec: spec.filename };

    const id = crypto.randomUUID();
    const job = await startJob(id, options, request.photo);
    const done = await pollJob(job.job_id, job.poll_after_ms);
    const response = await fetch(proxy + '/jobs/' + encodeURIComponent(done.job_id || job.job_id) + '/preview');
    if (!response.ok) throw new Error('Could not load your artwork (' + response.status + '). Please try again.');
    const blob = await response.blob();
    return { imageUrl: URL.createObjectURL(blob), artworkId: id, jobId: done.job_id || job.job_id };
  }

  // ---- catalog (imported Shopify products via the app proxy) ------------------------------
  // The catalog is slow to build server-side, so it is prefetched on every Pet Cameo page and kept
  // in sessionStorage: the shop page then renders instantly. A cached copy is used for up to
  // CATALOG_TTL and refreshed in the background once it is older than CATALOG_REFRESH.
  const CATALOG_KEY = 'petcameo:catalog';
  const CATALOG_TTL = 5 * 60 * 1000;
  const CATALOG_REFRESH = 60 * 1000;
  let catalogPromise = null;

  function readCatalogCache() {
    try {
      const cached = JSON.parse(sessionStorage.getItem(CATALOG_KEY) || 'null');
      if (cached && cached.data && Date.now() - cached.at < CATALOG_TTL) return cached;
    } catch { /* storage blocked or corrupt */ }
    return null;
  }

  function preloadCatalogImages(catalog) {
    (catalog.products || []).forEach(product => { if (product.image) new Image().src = product.image; });
  }

  function fetchCatalog() {
    if (!catalogPromise) {
      catalogPromise = getJson(proxy + '/products')
        .then(data => {
          try { sessionStorage.setItem(CATALOG_KEY, JSON.stringify({ at: Date.now(), data })); } catch { /* storage blocked */ }
          preloadCatalogImages(data);
          return data;
        })
        .finally(() => { catalogPromise = null; });
    }
    return catalogPromise;
  }

  // { fresh: true } skips the cache (used before adding to the cart, where stock must be current).
  function loadCatalog(options) {
    const cached = !(options && options.fresh) && readCatalogCache();
    if (cached) {
      if (Date.now() - cached.at > CATALOG_REFRESH) fetchCatalog().catch(() => {});
      return Promise.resolve(cached.data);
    }
    return fetchCatalog();
  }
  const optionValue = (variant, test) => {
    const key = Object.keys(variant.options || {}).find(test);
    return key ? variant.options[key] : '';
  };

  function toShopProduct(product, currency) {
    const available = product.variants.filter(variant => variant.available);
    const colors = [], seen = new Set();
    available.forEach(variant => {
      const name = optionValue(variant, colorKey);
      if (!name || seen.has(name)) return;
      seen.add(name);
      colors.push({ id: slug(name), name, hex: /^#[0-9a-f]{6}$/i.test(variant.color_hex || '') ? variant.color_hex : '#b6b6b0' });
    });
    const sizes = [...new Set(available.map(variant => optionValue(variant, sizeKey)).filter(Boolean))];
    const prices = available.map(variant => Number(variant.price));
    return {
      id: String(product.id),
      title: product.title,
      price: Math.min(...prices),
      currency,
      imageUrl: product.image || (window.PetCameoAssets && window.PetCameoAssets['PetImageDefault.svg']) || '',
      colors,
      sizes,
      // Which size/color pairs exist, so a size only offers its own colors (e.g. 11 oz vs 15 oz mugs).
      combos: available.map(variant => ({ size: optionValue(variant, sizeKey), colorId: slug(optionValue(variant, colorKey)) }))
    };
  }

  async function getShopContext() {
    let artwork = null;
    try { artwork = JSON.parse(sessionStorage.getItem('petcameo:artwork') || 'null'); } catch { /* storage blocked */ }
    const catalog = await loadCatalog();
    const products = (catalog.products || [])
      .filter(product => product.variants.some(variant => variant.available))
      .map(product => toShopProduct(product, catalog.currency || 'USD'));
    return { artwork, products };
  }

  // ---- cart -------------------------------------------------------------------------------
  const committed = new Set();
  async function commitSession(artworkId) {
    if (committed.has(artworkId)) return;
    const response = await fetch(proxy + '/sessions/' + encodeURIComponent(artworkId) + '/commit', { method: 'POST' });
    if (!response.ok) throw new Error('Could not save your design (' + response.status + '). Please try again.');
    committed.add(artworkId);
  }

  const post = (url, body) => fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify(body)
  });

  async function addVariant(variantId, quantity, properties) {
    const response = await post('/cart/add.js', { id: variantId, quantity, properties });
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new Error((body && (body.description || body.message)) || 'Could not add this item to your cart.');
    }
  }

  async function addToCart(selection) {
    const artwork = selection.artwork;
    if (!artwork || !artwork.artworkId) throw new Error('Create your pet’s artwork first, then choose your product.');
    const catalog = await loadCatalog({ fresh: true });
    const product = (catalog.products || []).find(item => String(item.id) === selection.productId);
    if (!product) throw new Error('That product isn’t available right now.');
    const variant = product.variants.find(item =>
      (!selection.colorId || slug(optionValue(item, colorKey)) === selection.colorId) &&
      (!selection.size || optionValue(item, sizeKey) === selection.size));
    if (!variant || !variant.available) throw new Error('That color and size combination is sold out. Please choose another.');
    await commitSession(artwork.artworkId);
    const properties = { _generated_image_id: artwork.artworkId };
    if (artwork.jobId) properties._job_id = artwork.jobId;
    await addVariant(variant.id, selection.quantity || 1, properties);
    const cart = await getJson('/cart.js');
    return { success: true, cartCount: cart.item_count, cartUrl: routes.cart || '/cart' };
  }

  async function getCart() {
    const cart = await getJson('/cart.js');
    let catalog = null;
    try { catalog = await loadCatalog(); } catch { /* lines still render without option lists */ }
    let stored = null;
    try { stored = JSON.parse(sessionStorage.getItem('petcameo:artwork') || 'null'); } catch { /* storage blocked */ }

    const items = cart.items.map(line => {
      const options = {};
      (line.options_with_values || []).forEach(option => { options[option.name] = option.value; });
      const product = catalog && (catalog.products || []).find(item => String(item.id) === String(line.product_id));
      const size = optionValue({ options }, sizeKey);
      const colorName = optionValue({ options }, colorKey);
      let sizes = [], colors = [], combos;
      if (product) {
        const available = product.variants.filter(variant => variant.available);
        combos = available.map(variant => ({ size: optionValue(variant, sizeKey), colorId: slug(optionValue(variant, colorKey)) }));
        sizes = [...new Set(available.map(variant => optionValue(variant, sizeKey)).filter(Boolean))];
        colors = [...new Map(available.map(variant => optionValue(variant, colorKey)).filter(Boolean).map(name => [slug(name), { id: slug(name), name }])).values()];
      }
      if (size && !sizes.includes(size)) sizes.push(size);
      if (colorName && !colors.some(color => color.id === slug(colorName))) colors.push({ id: slug(colorName), name: colorName });
      const properties = line.properties || {};
      const artworkId = properties._generated_image_id;
      let imageUrl = '';
      if (stored && stored.artworkId === artworkId) imageUrl = stored.imageUrl;
      else if (properties._job_id) imageUrl = proxy + '/jobs/' + encodeURIComponent(properties._job_id) + '/preview';
      return {
        id: line.key,
        title: line.product_title,
        productId: String(line.product_id),
        price: line.final_price / 100,
        quantity: line.quantity,
        size, sizes, combos,
        colorId: slug(colorName), colors,
        imageUrl: (line.featured_image && line.featured_image.url) || line.image || '',
        artwork: { artworkId, jobId: properties._job_id, imageUrl }
      };
    });
    return { items, currency: cart.currency || 'USD' };
  }

  async function updateCartLine(id, changes) {
    const cart = await getJson('/cart.js');
    const line = cart.items.find(item => item.key === id);
    if (!line) throw new Error('That item is no longer in your cart.');
    const quantity = changes.quantity !== undefined ? changes.quantity : line.quantity;
    if (changes.size !== undefined || changes.colorId !== undefined) {
      const catalog = await loadCatalog();
      const product = (catalog.products || []).find(item => String(item.id) === String(line.product_id));
      if (!product) throw new Error('That product isn’t available right now.');
      const current = {};
      (line.options_with_values || []).forEach(option => { current[option.name] = option.value; });
      const wantSize = changes.size !== undefined ? changes.size : optionValue({ options: current }, sizeKey);
      const wantColor = changes.colorId !== undefined ? changes.colorId : slug(optionValue({ options: current }, colorKey));
      const matches = item => (!wantColor || slug(optionValue(item, colorKey)) === wantColor) && (!wantSize || optionValue(item, sizeKey) === wantSize);
      let variant = product.variants.find(item => item.available && matches(item));
      // Changing the size to one that doesn't come in the current color moves to that size's first available color.
      if (!variant && changes.size !== undefined && changes.colorId === undefined) {
        variant = product.variants.find(item => item.available && optionValue(item, sizeKey) === wantSize);
      }
      if (!variant || !variant.available) throw new Error('That color and size combination is sold out.');
      if (String(variant.id) !== String(line.variant_id)) {
        // Add the new variant first so a failure leaves the original line untouched.
        await addVariant(variant.id, quantity, line.properties || {});
        await post('/cart/change.js', { id, quantity: 0 });
        return getCart();
      }
    }
    const response = await post('/cart/change.js', { id, quantity });
    if (!response.ok) throw new Error('Could not update your cart.');
    return getCart();
  }

  async function removeCartLine(id) {
    const response = await post('/cart/change.js', { id, quantity: 0 });
    if (!response.ok) throw new Error('Could not remove that item.');
    return getCart();
  }

  async function checkout() {
    return { checkoutUrl: routes.checkout || '/checkout' };
  }

  // Warm the catalog while the customer is still on Create (or anywhere else on the site).
  loadCatalog().then(preloadCatalogImages, () => {});

  // openShop is intentionally left undefined: Create's built-in handoff stores the artwork in
  // sessionStorage and then opens routes.shop.
  window.PetCameoAPI = Object.assign(window.PetCameoAPI || {}, {
    generateArtwork, getStyles, getShopContext, addToCart, getCart, updateCartLine, removeCartLine, checkout
  });
})();
